// Global "pull down from the top edge to restore the last-watched media"
// gesture. Mirrors how iOS Safari / Chrome handle pull-to-refresh BUT
// for our mini-player instead of a page reload.
//
// Triggers when:
//   • Touch starts within the top TOP_EDGE_PX of the viewport
//   • User drags down at least RESTORE_PX
//   • Drag is mostly-vertical (|dx| < |dy| * 0.6)
//   • The mini-player is NOT already open (open === false)
//   • The MiniPlayerProvider has a saved "last played" blob (resume() exists)
//
// While the user is dragging we show a soft pill at the top with the
// title of the track that's about to come back ("⏯ Resume {title}"),
// giving them confidence about what's about to happen. If they let go
// before reaching the threshold, the pill snaps away — no surprises.
import React, { useCallback, useRef, useState } from "react";
import { useMiniPlayer } from "../context/MiniPlayerContext";

const TOP_EDGE_PX = 36;
const RESTORE_PX = 90;

export default function GlobalPullDownToResume() {
  const { open, resume } = useMiniPlayer();
  const start = useRef(null);
  const [dy, setDy] = useState(0);
  const [armed, setArmed] = useState(false);
  const [lastTitle, setLastTitle] = useState("");

  const peekLastTitle = useCallback(() => {
    try {
      const raw = window.localStorage.getItem("hw:last-played");
      if (!raw) return "";
      const last = JSON.parse(raw);
      return (last && last.title) || "";
    } catch (_) {
      return "";
    }
  }, []);

  const onTouchStart = useCallback((e) => {
    if (open) return; // already restored — nothing to do
    const t = e.touches && e.touches[0];
    if (!t) return;
    if (t.clientY > TOP_EDGE_PX) return; // not from the top edge
    const title = peekLastTitle();
    if (!title) return; // nothing saved to resume
    start.current = { x: t.clientX, y: t.clientY };
    setArmed(true);
    setLastTitle(title);
  }, [open, peekLastTitle]);

  const onTouchMove = useCallback((e) => {
    if (!armed || !start.current) return;
    const t = e.touches && e.touches[0];
    if (!t) return;
    const dx = t.clientX - start.current.x;
    const movedY = t.clientY - start.current.y;
    if (movedY <= 0 || Math.abs(dx) > Math.abs(movedY) * 0.6) {
      setDy(0);
      return;
    }
    // Slight rubber-band so it feels like dragging a curtain down.
    setDy(Math.min(160, movedY * 0.85));
  }, [armed]);

  const onTouchEnd = useCallback(() => {
    if (armed && dy >= RESTORE_PX) {
      try { resume(); } catch (_) {}
    }
    start.current = null;
    setArmed(false);
    setDy(0);
  }, [armed, dy, resume]);

  // Soft visual indicator — only renders while the user is actively
  // dragging. The wrapper has `pointer-events-none` so it never
  // interferes with the touch handlers on the window.
  const peek = dy > 6 && armed;
  const willCommit = dy >= RESTORE_PX;

  // Mount the listeners as React touch handlers on a full-viewport
  // overlay that's also pointer-events-none — touches still pass
  // through to whatever's below (links, scrolls, etc.) because of
  // the pointer-events:none, but React's bubbling synthetic event
  // system still calls our handlers.
  // NOTE: bubble-through touch handlers via window listener instead
  // because a pointer-events:none overlay DOESN'T receive touch
  // events itself (events go to the elements below).
  React.useEffect(() => {
    if (open) return; // disable listeners entirely while resumed/full
    const opts = { passive: true };
    const wStart = (ev) => onTouchStart(ev);
    const wMove = (ev) => onTouchMove(ev);
    const wEnd = (ev) => onTouchEnd(ev);
    window.addEventListener("touchstart", wStart, opts);
    window.addEventListener("touchmove", wMove, opts);
    window.addEventListener("touchend", wEnd, opts);
    window.addEventListener("touchcancel", wEnd, opts);
    return () => {
      window.removeEventListener("touchstart", wStart);
      window.removeEventListener("touchmove", wMove);
      window.removeEventListener("touchend", wEnd);
      window.removeEventListener("touchcancel", wEnd);
    };
  }, [open, onTouchStart, onTouchMove, onTouchEnd]);

  if (!peek) return null;
  return (
    <div
      className="fixed top-0 left-0 right-0 z-[200] flex justify-center pointer-events-none"
      style={{
        transform: `translateY(${Math.max(0, dy - 8)}px)`,
        transition: "none",
      }}
      data-testid="pull-down-to-resume-peek"
    >
      <div
        className={`mt-2 inline-flex items-center gap-2 px-4 h-9 rounded-full shadow-lg backdrop-blur-md transition-colors ${
          willCommit ? "bg-terracotta text-cream" : "bg-ink/85 text-cream"
        }`}
      >
        <span className="text-[11px] tracking-[0.18em] uppercase font-bold">
          {willCommit ? "Release to resume" : "Pull down to resume"}
        </span>
        <span className="text-[11px] truncate max-w-[150px] opacity-90">{lastTitle}</span>
      </div>
    </div>
  );
}
